/**
 * @file `Secrets` — namespacer for `admin.secrets.*` corresponding to
 *       the `/secrets` panel in the admin shell.
 * @module @labcraft/admin/src/client/secrets
 *
 * One `CoreLifecycle`-based namespacer per UI panel (per the workspace
 * Client-shape rule). Owns no transport of its own — the parent
 * `Admin` adopts the shared `HttpTransport` and hands a reference here
 * at construction. Logs and metrics bubble under `admin.secrets.*`.
 */

import { CoreLifecycle } from '@labcraft/core'
import type { RotateSecretRequest } from '@labcraft/openbao/shared'
import type { HttpTransport } from './index.ts'
import {
  RotateSecretRoute,
  SecretsListRoute,
  type RotateSecretResponse,
  type SecretsListResponse,
} from '../shared/secrets_routes.ts'

/**
 * Read + write namespacer for the Secrets panel. Held on the
 * frontend `Admin` Client; React components reach it via
 * `useClient<Admin>().secrets`.
 */
export class Secrets extends CoreLifecycle<{ id?: string }> {
  private readonly http: HttpTransport

  constructor(http: HttpTransport) {
    super({ id: 'secrets' })
    this.http = http
  }

  /**
   * Fetch the per-secret declarations + rotation metadata. Values
   * themselves are NEVER returned by the daemon — the UI is
   * write-only by construction.
   */
  async list(): Promise<SecretsListResponse> {
    return this.http.request(SecretsListRoute)
  }

  /**
   * Rotate one secret. The new value travels in the request body and
   * never appears in any response. Returns the new version +
   * timestamp so the panel row can update without a follow-up GET.
   *
   * @param body - `{ path, value }` — the canonical secret path and
   *   the operator-supplied new value.
   */
  async rotate(body: RotateSecretRequest): Promise<RotateSecretResponse> {
    return this.http.request(RotateSecretRoute, { body })
  }
}
